/**
 * ResizeHandle Component
 * Draggable handle for resizing the Library Sidebar
 */

import { useCallback, useEffect, useRef, useState } from 'react';

interface ResizeHandleProps {
  onResize: (width: number) => void;
  minWidth: number;
  maxWidth: number;
}

export function ResizeHandle({ onResize, minWidth, maxWidth }: ResizeHandleProps) {
  const [isDragging, setIsDragging] = useState(false);
  const startXRef = useRef(0);
  const startWidthRef = useRef(0);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();

    // Find the closest aside element to get current width
    const aside = (e.currentTarget as HTMLElement).closest('aside');
    if (!aside) return;

    const currentWidth = aside.getBoundingClientRect().width;

    // Capture start position and width
    startXRef.current = e.clientX;
    startWidthRef.current = currentWidth;
    setIsDragging(true);
  }, []);

  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      // Calculate delta from start position
      const deltaX = e.clientX - startXRef.current;
      const newWidth = startWidthRef.current + deltaX;

      // Clamp width to min/max constraints
      const clampedWidth = Math.min(Math.max(newWidth, minWidth), maxWidth);

      // Update width via callback
      onResize(clampedWidth);
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    // Attach document-level listeners for smooth dragging
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    // Cleanup listeners on unmount or when dragging ends
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, onResize, minWidth, maxWidth]);

  return (
    <div
      className={`
        absolute top-0 right-0 bottom-0
        w-1 z-20 cursor-col-resize
        transition-colors duration-150
        ${isDragging ? 'bg-emerald-500/50' : 'bg-transparent hover:bg-emerald-500/30'}
      `}
      onMouseDown={handleMouseDown}
    >
      {/* Wider invisible hit area for better UX */}
      <div className="absolute inset-y-0 -left-1 -right-1" />
    </div>
  );
}
